import React, { useContext } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { formatCurrency, formatPriceLabel } from '@/core/utils/currency';
import { descriptorTitle } from '@/features/rapidstor/helpers';
import { JourneyContext } from '@/features/rapidstor/providers/JourneyProvider';
import { DescriptorSwitchConfirmProps } from '@/journey-based-listing/components/UnitSelect/OptionSelector';

import classes from './DealToggleBar.module.scss';

export default function DealToggleBarConfirm({
    pendingSwitch,
    onConfirm,
    onCancel,
    location,
}: DescriptorSwitchConfirmProps) {
    const { t } = useTranslation();
    const { selectedDescriptor, selectedUnitType } = useContext(JourneyContext);

    const currentDescriptorName = selectedDescriptor
        ? descriptorTitle(selectedDescriptor, location)[1]
        : '';
    const currentRate = selectedUnitType?.rates.rate ?? 0;

    const newDescriptorName = descriptorTitle(pendingSwitch.descriptor, location)[1];
    const newRate = pendingSwitch.unitType.rates.discountedRate ?? pendingSwitch.unitType.rates.rate;
    const dealCarriedOver = pendingSwitch.matchedDeal !== null;

    return (
        <AnimatePresence>
            <motion.div
                key="switch-confirm-backdrop"
                className={classes.modal_backdrop}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.2 }}
                onClick={onCancel}
            >
                <motion.div
                    key="switch-confirm-modal"
                    className={classes.modal}
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    onClick={(e) => e.stopPropagation()}
                >
                    <span className={`${classes.modal_title} ${classes['header-3']}`}>
                        {t('labels:switch_unit_size', 'Switch Unit Size?')}
                    </span>

                    <div className={classes.comparison}>
                        <div className={classes.comparison_card}>
                            <span className={classes.comparison_label}>
                                {t('labels:current', 'Current')}
                            </span>
                            <span className={classes.comparison_name} data-rs-setting-dynamic="descriptorTitle">
                                {currentDescriptorName}
                            </span>
                            <span className={classes.comparison_rate}>
                                {formatPriceLabel(formatCurrency(currentRate, location), location)}
                            </span>
                        </div>

                        <span className={classes.comparison_arrow}>&rarr;</span>

                        <div className={classes.comparison_card}>
                            <span className={classes.comparison_label}>
                                {t('labels:new', 'New')}
                            </span>
                            <span className={classes.comparison_name} data-rs-setting-dynamic="descriptorTitle">
                                {newDescriptorName}
                            </span>
                            <span className={classes.comparison_rate}>
                                {formatPriceLabel(formatCurrency(newRate, location), location)}
                            </span>
                        </div>
                    </div>

                    {!dealCarriedOver && (
                        <div className={classes.warning_bar}>
                            {t(
                                'labels:deal_not_available_for_size',
                                'Your current deal is not available for this size',
                            )}
                        </div>
                    )}

                    {dealCarriedOver && (
                        <div className={classes.success_bar}>
                            {t(
                                'labels:deal_applied_to_new_size',
                                'Deal applied to new size',
                            )}
                        </div>
                    )}

                    <div className={classes.modal_footer}>
                        <button
                            type="button"
                            className={classes.btn_cancel}
                            onClick={onCancel}
                        >
                            {t('labels:cancel', 'Cancel')}
                        </button>
                        <button
                            type="button"
                            className={classes.btn_confirm}
                            onClick={onConfirm}
                        >
                            {t('labels:confirm', 'Confirm')}
                        </button>
                    </div>
                </motion.div>
            </motion.div>
        </AnimatePresence>
    );
}
